import { CardioOrResistance, WorkoutType } from 'fitbeat.models';
import { AreaCategory, isStaticCardioZone } from '../../config/zone-mappings';
import { ITimeSlot } from '../gymscheduling';
import { ITimeSlotViewModel } from './time-slot-view-model';

export enum UnavailableReason {
  existingBookingForDay = 'existingBookingForDay',
  noAvailableSpaceInGym = 'noAvailableSpaceInGym',
  partnerBooking = 'partnerBooking',
  notCurrentDay = 'notCurrentDay',
  notEnabledOnGym = 'notEnabledOnGym',
  notAvailableForStaticZones = 'notAvailableForStaticZones',
}

export type StudioTimeSlotStatus = 'Available' | 'Fully Booked' | 'Not Available';
export type AccordionCondition = 'Open' | 'Closed' | 'Disabled';
export type TimeSlotWarningText = 'NoPartnerBookingError'
  | 'StaticCardioZoneGoalNotAlignedError'
  | 'AlreadyHaveBookingTodayError'
  | 'GoalNotAlignedError' // deprecated, kept for compatibility with <1.0.275
  | 'PowerZoneWarning'
  | 'BoxingZoneGoalNotAlignedError';

export interface INoOnDemandSlotAvailable {
  unavailableReason: UnavailableReason;
}

export interface IOnDemandSlotAvailable {
  unavailableReason?: undefined;
  slots: ITimeSlot[],
}

export type OnDemandBookingSlotData = INoOnDemandSlotAvailable | IOnDemandSlotAvailable;

export interface ISelectStudioClassTimeData {
  classTimes: ITimeSlotViewModel[];
  onDemand: OnDemandBookingSlotData;
}

export interface IWorkoutTypeClassTimeData {
  classTimes: ITimeSlotViewModel[];
  workoutType: WorkoutType;
}

export interface IAvailableStudioTimeSlots {
  areaCategory: AreaCategory;
  availableTimeSlots: IWorkoutTypeClassTimeData[];
  status: StudioTimeSlotStatus;
  accordionCondition: AccordionCondition;
  warning?: TimeSlotWarningText;
}

export function determineStatus(
  availableTimes: IWorkoutTypeClassTimeData[],
  areaCategory: AreaCategory,
  withPartner: boolean,
): StudioTimeSlotStatus {
  const noSlotsAvailable = !availableTimes.some((timeslots) => timeslots.classTimes.length > 0);
  const staticCardioAreaWithPartner = (isStaticCardioZone(areaCategory) && withPartner);
  if (noSlotsAvailable || staticCardioAreaWithPartner) {
    return 'Not Available';
  }

  for (const timeslotsForWorkoutType of availableTimes) {
    for (const classTimeslot of timeslotsForWorkoutType.classTimes) {
      if (classTimeslot.amountOfOpenSlots > 0) {
        return 'Available';
      }
    }
  }
  return 'Fully Booked';
}

export function determineAccordionCondition(
  status: StudioTimeSlotStatus,
  areaCategory: AreaCategory,
): AccordionCondition {
  if (areaCategory === 'circuit') {
    if (status !== 'Available') {
      return 'Disabled';
    }
    return 'Open';
  }
  if (status !== 'Available') {
    return 'Disabled';
  }
  return 'Closed';
}

export function determineWarningText(
  areaCategory: AreaCategory,
  withPartner: boolean,
  userGoal: CardioOrResistance,
  userHasABookingOnThisDay: boolean,
  useDeprecatedGoalNotAlignedError: boolean,
): TimeSlotWarningText | undefined {
  if (userHasABookingOnThisDay) {
    return 'AlreadyHaveBookingTodayError';
  }

  if (areaCategory === 'circuit') {
    return undefined;
  }
  if (withPartner && isStaticCardioZone(areaCategory)) {
    return 'NoPartnerBookingError';
  }
  if (areaCategory === 'power') {
    return 'PowerZoneWarning';
  }
  if (userGoal === CardioOrResistance.Balance && areaCategory === 'boxing') {
    return 'BoxingZoneGoalNotAlignedError';
  }
  if (userGoal !== CardioOrResistance.Cardio && isStaticCardioZone(areaCategory)) {
    if (useDeprecatedGoalNotAlignedError) {
      return 'GoalNotAlignedError';
    }
    return 'StaticCardioZoneGoalNotAlignedError';
  }
  return undefined;
}

export function isOnDemandAvailable(data: OnDemandBookingSlotData): data is IOnDemandSlotAvailable {
  return data.unavailableReason === undefined;
}
